import * as React from 'react';
import classnames from 'classnames';
import { isEmpty, isEqual } from 'lodash';

import Link from 'libs/router/Link';

import CloudinaryImage from 'components/CloudinaryImage';

import { expandDataProps } from 'utils/helper/common';
import { getImageFallback } from 'utils/view';

import './entity-avatar.scss';

import type { Data, EntitySize, ImageSize } from 'types';
import { Image } from 'types';

export type EntityAvatarProps = {
  link?: string | null,
  bordered?: boolean,
  size: EntitySize,
  form: 'round' | 'square',
  background?: 'default' | 'primary',
  openInNewTab: boolean,
  customSize?: ImageSize,
  applyCustomElementSize: boolean,
  hideTitle?: boolean,
  data?: Data,
  className: string,
  isClickable?: boolean,
  onClick?: (a: React.SyntheticEvent<HTMLElement>) => void
};

export const EntityAvatarDefaultProps: EntityAvatarProps = {
  size: 'medium',
  form: 'round',
  openInNewTab: false,
  className: '',
  applyCustomElementSize: true
};

// entity can be anything if it implements this interface
interface EntityInterface {
  displayName?: string,
  firstName?: string,
  lastName?: string,
  image?: Image,
  fallbackText?: string
}

type Props<T> = EntityAvatarProps & {
  entity: T | undefined | null
};

class EntityAvatar<T extends EntityInterface> extends React.Component<Props<T>> {
  static defaultProps = EntityAvatarDefaultProps;

  shouldComponentUpdate(nextProps: Props<T>): boolean {
    const { entity, link, bordered, size, form, openInNewTab, customSize, className } = this.props;

    return !isEqual(entity, nextProps.entity) ||
      !isEqual(customSize, nextProps.customSize) ||
      !(link === nextProps.link) ||
      !(bordered === nextProps.bordered) ||
      !(size === nextProps.size) ||
      !(form === nextProps.form) ||
      !(openInNewTab === nextProps.openInNewTab) ||
      !(className === nextProps.className);
  }

  render() {
    const {
      entity,
      link,
      bordered,
      background,
      size,
      customSize,
      applyCustomElementSize,
      form,
      openInNewTab,
      hideTitle,
      data,
      className,
      isClickable,
      onClick
    } = this.props;

    if (!entity) {
      return null;
    }

    const customDimensions: Partial<typeof customSize> = customSize || {};
    const entityDisplayName = entity.displayName;
    const imageFallback = getImageFallback(entity);

    const avatarClasses = classnames({
      'entity-avatar': true,
      'entity-avatar--bordered': bordered,
      'entity-avatar--clickable': link || isClickable,
      [`entity-avatar--size-${size}`]: size && isEmpty(customDimensions),
      [`entity-avatar--form-${form}`]: form,
      [`entity-avatar--background-${String(background)}`]: background,
      [className]: Boolean(className)
    });

    const fallbackClasses = classnames({
      'entity-avatar__fallback': true,
      'text-overflow-ellipsis': true,
      [`entity-avatar__fallback--size-${size}`]: size && isEmpty(customDimensions)
    });

    const content = (entity.image && entity.image.url) ? (
      <CloudinaryImage
        image={entity.image}
        width={customDimensions.width ?? 74}
        height={customDimensions.height ?? 74}
        resizable
      />
    ) :
      <span className={fallbackClasses}>{imageFallback}</span>;

    const avatarProps = {
      className: avatarClasses,
      title: hideTitle ? '' : entityDisplayName,
      ...(applyCustomElementSize ? { style: customDimensions } : null)
    };

    if (!link) {
      return (
        <span
          {...avatarProps}
          {...expandDataProps(data)}
        >
          {content}
        </span>
      );
    }

    if (openInNewTab) {
      return (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          {...avatarProps}
          {...expandDataProps(data)}
          onClick={onClick}
        >
          {content}
        </a>
      );
    }

    return (
      <Link
        to={link}
        {...avatarProps}
        {...expandDataProps(data)}
      >
        {content}
      </Link>
    );
  }
}

export default EntityAvatar;
